import { API_BASE_URL, apiFetch } from "./api";

// Active company data for the export and print header: text data from the ini table + logo.
// Cached per session; invalidateBranding() is called after a logo change or a refresh of company data.
let cached = null;

export function invalidateBranding() {
  cached = null;
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

function imageSize(dataUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

async function build() {
  const res = await apiFetch("/profile.php");
  const json = await res.json();
  const company = json?.success ? (json.firme || []).find((f) => f.current) : null;
  if (!company) return { info: null, logo: null };

  let info = company.info;
  if (!info) {
    // Data has not been fetched yet: try once automatically, with no error if it fails.
    try {
      const r = await (await apiFetch(`/company_info_refresh.php?id=${company.id}`, { method: "POST" })).json();
      if (r?.success) info = r.info;
    } catch {
      /* export works without a header too */
    }
  }

  let logo = null;
  if (company.has_logo) {
    try {
      const img = await apiFetch(`/image_get.php?type=company&id=${company.id}&v=${company.logo_v}`);
      const blob = await img.blob();
      const dataUrl = await blobToDataUrl(blob);
      const size = await imageSize(dataUrl);
      if (size) logo = { dataUrl, mime: blob.type, ...size };
    } catch {
      /* no logo */
    }
  }
  return { info, logo };
}

/** Never throws: export and print must work even when the header is unavailable. */
export function getBranding() {
  if (!cached) cached = build().catch(() => ({ info: null, logo: null }));
  return cached;
}

/** Text lines for the header: name, address, contact. */
export function brandingLines(info) {
  if (!info) return [];
  const addr = [info.Address, [info.Zip, info.City].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  const contact = [info.Telephone, info.EMail, info.WebPage].filter(Boolean).join("  |  ");
  const ids = [info.Code && `PIB: ${info.Code}`, info.Number && `MB: ${info.Number}`, info.BankAccount && `Žiro račun: ${info.BankAccount}`]
    .filter(Boolean)
    .join("  |  ");
  return [info.Name, addr, contact, ids].filter(Boolean);
}

export const imageUrl = (type, id, v) => `${API_BASE_URL}/image_get.php?type=${type}${id ? `&id=${id}` : ""}&v=${v || 0}`;

// Product logo for the bottom-right corner of PDF and print. A separate file from the one used in the app header:
// put /logo-print.png on the server to set a dedicated one; without it the dark app logo is used.
const FOOTER_LOGO_CANDIDATES = ["/logo-print.png", "/logo-dark.png"];
let footerLogoCached = null;

async function loadFooterLogo() {
  for (const path of FOOTER_LOGO_CANDIDATES) {
    try {
      const res = await fetch(`${process.env.PUBLIC_URL || ""}${path}`, { cache: "force-cache" });
      const type = res.headers.get("content-type") || "";
      if (!res.ok || !type.startsWith("image/")) continue; // an SPA fallback returns index.html, not an image
      const blob = await res.blob();
      const dataUrl = await blobToDataUrl(blob);
      const size = await imageSize(dataUrl);
      if (size) return { dataUrl, mime: blob.type, ...size };
    } catch {
      /* try the next candidate */
    }
  }
  return null;
}

/** Never throws; null when no logo file exists. */
export function getFooterLogo() {
  if (!footerLogoCached) footerLogoCached = loadFooterLogo().catch(() => null);
  return footerLogoCached;
}
